import { Injectable, inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@/core/services/api/api.service';
import { GarantiesData } from '../../interfaces/credit.interface';

@Injectable({ providedIn: 'root' })
export class CreditGarantieService {
  private readonly api = inject(ApiService);
  private readonly endpoint = '/credit';

  getGarantiesDemande(ref: string) {
    return this.api
      .get<{ demande: GarantiesData }>(this.endpoint + '/getGarantiesDemande/' + ref)
      .pipe(
        map((res) => res.demande),
        catchError((err) => throwError(() => err)),
      );
  }

  // ── Actifs et garanties ───────────────────────────────────────────────────
  saveActifGarantie(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveActifGarantie', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteActifGarantie(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteActifGarantie/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Cautions solidaires ───────────────────────────────────────────────────
  saveCautionSolidaire(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrCaution', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteCautionSolidaire(id: number | string) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteCaution/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Upload photo profil caution ─────────────────────────────────────────
  updatePhotoProfilCaution(cautionId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id', cautionId.toString());
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/updateProfilImgCaution', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Upload image caution ────────────────────────────────────────────────
  saveImageCaution(cautionId: number, libelle: string, file: File) {
    const formData = new FormData();
    formData.append('type', 'CAUTION');
    formData.append('libelle', libelle);
    formData.append('description', libelle);
    formData.append('photo', file);
    formData.append('element', cautionId.toString());
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveImageGarantie', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Upload document caution ─────────────────────────────────────────────
  saveDocumentCaution(cautionId: number, libelle: string, file: File) {
    const formData = new FormData();
    formData.append('type', 'CAUTION');
    formData.append('libelle', libelle);
    formData.append('description', libelle);
    formData.append('file', file);
    formData.append('element', cautionId.toString());
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveDocumentGarantie', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Suppression image / document caution ───────────────────────────────────
  deleteImageCaution(imageId: number | string) {
    return this.api
      .post<{ status: number }>(this.endpoint + '/delete_image_garantie', { image: imageId })
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteDocumentCaution(docId: number | string) {
    return this.api
      .post<{ status: number }>(this.endpoint + '/delete_doc_garantie', { document: docId })
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Upload photo profil caution — FormData: refDemande, crCaution, photoProfil */
  uploadPhotoCaution(formData: FormData) {
    return this.api
      .postFormData<{ status: number }>(this.endpoint + '/updateProfilImgCaution', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Upload image (photo) liée à une caution — FormData: type=CAUTION, element=id, libelle, description, photo */
  uploadImageGarantie(formData: FormData) {
    return this.api
      .postFormData<{ status: number }>(this.endpoint + '/saveImageGarantie', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Upload document lié à une caution — FormData: type=CAUTION, element=id, libelle, description, file */
  uploadDocumentGarantie(formData: FormData) {
    return this.api
      .postFormData<{ status: number }>(this.endpoint + '/saveDocGarantie', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Upload document pour authentification de gage véhicule — type=GARANTIE */
  saveDocGarantieAuth(vehiculeId: number, libelle: string, file: File) {
    const formData = new FormData();
    formData.append('type', 'GARANTIE');
    formData.append('libelle', libelle);
    formData.append('description', libelle);
    formData.append('file', file);
    formData.append('element', vehiculeId.toString());
    return this.api
      .postFormData<{ status: number }>(this.endpoint + '/saveDocGarantie', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Enregistrer l'authentification des gages — payload: { authGages: [{id, auth_gage}] } (parité legacy) */
  authentificationGage(authGages: { id: number; auth_gage: 0 | 1 }[]) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/authentification_gage', { authGages })
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Legacy parity: /choixGarantie — toggle garantie sur un actif */
  choixGarantie(data: { crActifGarantie: number; garantie: 1 | 2 }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/choixGarantie', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Legacy parity: /saveGarantieDemande */
  saveGarantieProposee(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveGarantieDemande', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Documents analyse ────────────────────────────────────────────────────
  uploadDocumentAnalyse(formData: FormData) {
    return this.api
      .postFormData<{ status: number; message?: string }>(this.endpoint + '/saveDocAnalyse', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteDocumentAnalyse(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteDocAnalyse/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }
}
